import { useCallback, useEffect, useState } from 'react';
import { Save, Check, Smartphone, Plus, Trash2, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { CONFIG_LOCALES, fillLocales, type ConfigLocale } from '@/lib/app-config/locales';
import { adminFetch } from '@/api';

type Locale = ConfigLocale;

interface ContactBlock {
  email: string;
  emailSubject: string;
  whatsapp: string;
  whatsappMessage: string;
}

interface AboutBlock {
  tagline: string;
  copyright: string;
}

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

type ContactMap = Record<Locale, ContactBlock>;
type AboutMap = Record<Locale, AboutBlock>;
type FaqMap = Record<Locale, FaqItem[]>;

type TabKey = 'contact' | 'about' | 'faq';

const EMPTY_CONTACT: ContactBlock = {
  email: '',
  emailSubject: '',
  whatsapp: '',
  whatsappMessage: '',
};

const EMPTY_ABOUT: AboutBlock = { tagline: '', copyright: '' };

const emptyContactMap = (): ContactMap => fillLocales<ContactBlock>(null, () => ({ ...EMPTY_CONTACT }));
const emptyAboutMap = (): AboutMap => fillLocales<AboutBlock>(null, () => ({ ...EMPTY_ABOUT }));
const emptyFaqMap = (): FaqMap => fillLocales<FaqItem[]>(null, () => []);

async function loadPage<T>(slug: string, fallback: T): Promise<T> {
  const res = await adminFetch(`/api/pages?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) return fallback;
  const data = (await res.json()) as { page?: { content?: string } };
  if (!data.page?.content) return fallback;
  try {
    return JSON.parse(data.page.content) as T;
  } catch {
    return fallback;
  }
}

async function savePage(slug: string, title: string, payload: unknown): Promise<void> {
  const res = await adminFetch('/api/pages', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, title, content: JSON.stringify(payload) }),
  });
  if (!res.ok) throw new Error(`Save failed (${res.status})`);
}

export default function AppConfigPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('contact');
  const [locale, setLocale] = useState<Locale>('en');
  const [contact, setContact] = useState<ContactMap>(emptyContactMap());
  const [about, setAbout] = useState<AboutMap>(emptyAboutMap());
  const [faq, setFaq] = useState<FaqMap>(emptyFaqMap());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, a, f] = await Promise.all([
        loadPage<ContactMap>('config-contact', emptyContactMap()),
        loadPage<AboutMap>('config-about', emptyAboutMap()),
        loadPage<FaqMap>('config-faq', emptyFaqMap()),
      ]);
      // Saved configs may predate a newly added language — give it a blank tab.
      setContact(fillLocales<ContactBlock>(c, () => ({ ...EMPTY_CONTACT })));
      setAbout(fillLocales<AboutBlock>(a, () => ({ ...EMPTY_ABOUT })));
      setFaq(fillLocales<FaqItem[]>(f, () => []));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      if (activeTab === 'contact') {
        await savePage('config-contact', 'App Config — Contact', contact);
      } else if (activeTab === 'about') {
        await savePage('config-about', 'App Config — About', about);
      } else {
        await savePage('config-faq', 'App Config — FAQ', faq);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-emerald-400" /> App Config
          </h1>
          <p className="text-[13px] text-zinc-500 mt-1">
            Static content the Flutter app reads at boot. Edits go live after the app's next config fetch.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 text-black text-[13px] font-semibold px-4 py-2 hover:bg-emerald-400 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saved ? 'Saved' : 'Save this tab'}
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-[13px] px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-white/[0.06] pb-2">
        {(['contact', 'about', 'faq'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setActiveTab(k)}
            className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition ${
              activeTab === k ? 'bg-emerald-500/15 text-emerald-300' : 'text-zinc-500 hover:text-zinc-200'
            }`}
          >
            {k === 'contact' ? 'Contact Us' : k === 'about' ? 'About' : 'FAQ'}
          </button>
        ))}
        <div className="ml-auto flex gap-1 bg-white/[0.04] rounded-md p-1">
          {CONFIG_LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => setLocale(l)}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wider ${
                locale === l ? 'bg-emerald-500/20 text-emerald-300' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : activeTab === 'contact' ? (
        <ContactEditor value={contact[locale]} onChange={(v) => setContact({ ...contact, [locale]: v })} />
      ) : activeTab === 'about' ? (
        <AboutEditor value={about[locale]} onChange={(v) => setAbout({ ...about, [locale]: v })} />
      ) : (
        <FaqEditor value={faq[locale]} onChange={(v) => setFaq({ ...faq, [locale]: v })} />
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  multiline?: boolean;
}

function Field({ label, value, onChange, hint, multiline }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full rounded-md bg-white/[0.03] border border-white/[0.06] text-white text-[13px] px-3 py-2 focus:outline-none focus:border-emerald-500/50"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md bg-white/[0.03] border border-white/[0.06] text-white text-[13px] px-3 py-2 focus:outline-none focus:border-emerald-500/50"
        />
      )}
      {hint && <span className="block text-[11px] text-zinc-600 mt-1">{hint}</span>}
    </label>
  );
}

interface ContactEditorProps {
  value: ContactBlock;
  onChange: (v: ContactBlock) => void;
}

function ContactEditor({ value, onChange }: ContactEditorProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
      <Field label="Email" value={value.email} onChange={(v) => onChange({ ...value, email: v })} />
      <Field
        label="Email subject"
        value={value.emailSubject}
        onChange={(v) => onChange({ ...value, emailSubject: v })}
      />
      <Field label="WhatsApp number" value={value.whatsapp} onChange={(v) => onChange({ ...value, whatsapp: v })} />
      <Field
        label="WhatsApp prefill message"
        value={value.whatsappMessage}
        onChange={(v) => onChange({ ...value, whatsappMessage: v })}
      />
    </div>
  );
}

interface AboutEditorProps {
  value: AboutBlock;
  onChange: (v: AboutBlock) => void;
}

function AboutEditor({ value, onChange }: AboutEditorProps) {
  return (
    <div className="grid gap-4 max-w-3xl">
      <Field
        label="Tagline"
        value={value.tagline}
        onChange={(v) => onChange({ ...value, tagline: v })}
        hint="Shown under the app name on the About screen"
      />
      <Field
        label="Copyright"
        value={value.copyright}
        onChange={(v) => onChange({ ...value, copyright: v })}
        hint="Use {year} placeholder — client substitutes current year"
      />
    </div>
  );
}

interface FaqEditorProps {
  value: FaqItem[];
  onChange: (v: FaqItem[]) => void;
}

function FaqEditor({ value, onChange }: FaqEditorProps) {
  const updateAt = (idx: number, patch: Partial<FaqItem>) => {
    const next = value.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next);
  };

  const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const moveAt = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const add = () => {
    const nextId = `q${value.length + 1}`;
    onChange([...value, { id: nextId, question: '', answer: '' }]);
  };

  return (
    <div className="space-y-3 max-w-3xl">
      {value.map((f, idx) => (
        <div
          key={idx}
          className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-3"
        >
          <div className="flex items-center gap-2 mb-1">
            <input
              type="text"
              value={f.id}
              onChange={(e) => updateAt(idx, { id: e.target.value })}
              placeholder="id"
              className="w-24 rounded-md bg-white/[0.03] border border-white/[0.06] text-white text-[11px] px-2 py-1 font-mono uppercase"
            />
            <div className="flex-1" />
            <button onClick={() => moveAt(idx, -1)} className="p-1.5 rounded hover:bg-white/[0.05] text-zinc-500 hover:text-zinc-200" title="Move up">
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => moveAt(idx, 1)} className="p-1.5 rounded hover:bg-white/[0.05] text-zinc-500 hover:text-zinc-200" title="Move down">
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => removeAt(idx)} className="p-1.5 rounded hover:bg-red-500/10 text-zinc-500 hover:text-red-400" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <Field label="Question" value={f.question} onChange={(v) => updateAt(idx, { question: v })} />
          <Field label="Answer" value={f.answer} onChange={(v) => updateAt(idx, { answer: v })} multiline />
        </div>
      ))}
      <button
        onClick={add}
        className="inline-flex items-center gap-2 rounded-md border border-dashed border-white/[0.1] text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/40 text-[13px] px-4 py-2"
      >
        <Plus className="w-4 h-4" /> Add FAQ
      </button>
    </div>
  );
}
